import type { ComponentType } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { getCurrentPosition, getCurrentRole } from "../lib/auth";
import { FINANCE_PERMS, FINANCE_REPORT_PERMS, PERM, hasPerm } from "../lib/permissions";
import { POSITION_LABEL_LO } from "../lib/positions";
import { logout } from "../lib/session";
import { ROLE_LABEL_LO } from "../lib/roles";
import { initial } from "../lib/format";
import { TIER_LABEL_LO, canReach, tierOf, type Tier } from "../lib/tier";
import {
  AlertTriangle, BarChart3, CalendarIcon, Coins, FileText, History, Inbox, LogOut,
  MapIcon, Megaphone, PenLine, Settings, ShieldCheck, UserCog, Users,
} from "./icons";

interface NavEntry {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  /** ระดับต่ำสุดที่เห็นเมนูนี้ — คนระดับสูงกว่าเห็นด้วยเสมอ (ดู lib/tier.ts) */
  tier: Tier;
  /**
   * รหัสสิทธิ์ที่ต้องมี (ADR-0030) — รายการ = มีอย่างใดอย่างหนึ่งก็พอ · ไม่ตั้ง = ทุกคนที่ผ่าน `tier`
   * เช่น ນັດປຶກສາ ต้องมี SUP-01 (ADR-0027 ความลับ), ລາຍງານລາຍຮັບ ต้องมี Dashboard ການເງິນ
   */
  perm?: string | readonly string[];
}

interface NavGroup {
  label: string;
  items: NavEntry[];
}

/**
 * เมนูจัดเป็นกลุ่มตามงานที่ผู้ใช้กำลังทำ ไม่ใช่ตามระดับ — ผู้ดูแลระบบที่เห็นทุกเมนู
 * จะได้ไม่เจอรายการยาวเรียงมั่ว ๆ กลุ่มที่ไม่มีเมนูให้ระดับนั้นเห็นจะถูกซ่อนทั้งกลุ่ม
 *
 * เมนูที่ยังไม่มีหน้าจอจริงจะไม่ถูกใส่ในนี้ — เมนูที่กดแล้วไปหน้า 404
 * แย่กว่าไม่มีเมนู
 */
const NAV: NavGroup[] = [
  {
    label: "ພາບລວມ",
    items: [
      { to: "/dashboard", label: "Dashboard & ລາຍງານ", icon: BarChart3, tier: "district" },
    ],
  },
  {
    label: "ງານບໍລິການ",
    items: [
      // ผู้เซ็น (ຫົວໜ້າ/ຮອງຫົວໜ້າຕາແສງ) เห็นก่อนคิว — งานหลักของตำแหน่งนี้ตาม mockup v2
      { to: "/requests/sign", label: "ລໍເຊັນ ແລະ ປະທັບຕາ", icon: PenLine, tier: "district", perm: PERM.sign },
      { to: "/requests", label: "ຄິວຄຳຮ້ອງ", icon: Inbox, tier: "district" },
      { to: "/incidents", label: "ແຈ້ງເຫດ", icon: AlertTriangle, tier: "district" },
      {
        to: "/consultations",
        label: "ນັດປຶກສາ",
        icon: CalendarIcon,
        tier: "district",
        perm: PERM.consultations,
      },
      { to: "/district/revenue", label: "ລາຍງານລາຍຮັບ", icon: Coins, tier: "district", perm: FINANCE_PERMS },
    ],
  },
  {
    // ADR-0034 — ລາຍງານ 8 ແບບ + ບັນທຶກລາຍຈ່າຍ (บันทึกได้เฉพาะ FIN-06/07 ระดับตาแสง จอเช็คเอง)
    label: "ການເງິນ",
    items: [
      { to: "/reports/finance", label: "ລາຍງານການເງິນ", icon: BarChart3, tier: "district", perm: FINANCE_REPORT_PERMS },
      { to: "/finance/expenses", label: "ລາຍຈ່າຍ", icon: FileText, tier: "district", perm: FINANCE_REPORT_PERMS },
    ],
  },
  {
    label: "ຕັ້ງຄ່າບໍລິການ",
    items: [
      { to: "/district/catalog", label: "ລາຍການເອກະສານ/ລາຄາ", icon: FileText, tier: "district" },
      { to: "/system/announcements", label: "ຂ່າວ & ແຈ້ງການ", icon: Megaphone, tier: "system" },
      { to: "/system/services", label: "ບໍລິການເສີມ", icon: Settings, tier: "system" },
    ],
  },
  {
    label: "ພະນັກງານ ແລະ ພື້ນທີ່",
    items: [
      {
        to: "/district/staff",
        label: "ບັນຊີຕາແສງ/ນາຍບ້ານ",
        icon: UserCog,
        tier: "district",
        perm: PERM.manageAccounts,
      },
      { to: "/system/tasaengs", label: "ຕາແສງ ແລະ ບ້ານ", icon: MapIcon, tier: "system" },
      { to: "/users", label: "ຜູ້ໃຊ້ທົ່ວລະບົບ", icon: Users, tier: "system" },
    ],
  },
  {
    label: "ຄວາມປອດໄພ",
    items: [
      { to: "/district/audit", label: "ບັນທຶກການເຄື່ອນໄຫວ", icon: History, tier: "district" },
      { to: "/system/security", label: "ເຂົ້າລະບົບລົ້ມເຫຼວ", icon: ShieldCheck, tier: "system" },
    ],
  },
];

export function Shell() {
  const navigate = useNavigate();
  const role = getCurrentRole();
  const tier = tierOf(role);
  // ADR-0030: แสดงตำแหน่ง (claim `pos`) ก่อน — token เก่าที่ไม่มีตำแหน่งถอยไปใช้ป้าย role
  const position = getCurrentPosition();
  const roleLabel = position ? POSITION_LABEL_LO[position] : role ? ROLE_LABEL_LO[role] : "—";

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const groups = NAV.map((g) => ({
    ...g,
    items: g.items.filter((item) => canReach(tier, item.tier) && (!item.perm || hasPerm(item.perm))),
  })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="app-sidebar__brand">
          <img src="/images/mof_emblem.webp" alt="" className="app-sidebar__emblem" />
          <div className="app-sidebar__wordmark">
            <div className="micro">SDDS ADMIN</div>
            <div className="app-sidebar__tier">{tier ? TIER_LABEL_LO[tier] : "—"}</div>
          </div>
        </div>

        <nav className="app-sidebar__nav" aria-label="ເມນູຫຼັກ">
          {groups.map((group) => (
            <div key={group.label} className="nav-group" role="group" aria-label={group.label}>
              <div className="nav-group__label">{group.label}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/dashboard"}
                    aria-label={item.label}
                    className={({ isActive }) => ["nav-item", isActive ? "is-active" : ""].join(" ")}
                  >
                    <span className="nav-item__icon">
                      <Icon size={16} />
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="app-sidebar__footer">
          <div className="app-sidebar__who">
            <span className="app-sidebar__avatar" aria-hidden="true">
              {initial(roleLabel)}
            </span>
            <span className="app-sidebar__who-text">
              ເຂົ້າສູ່ລະບົບເປັນ
              <strong>{roleLabel}</strong>
            </span>
          </div>
          {/* ปุ่มออกจากระบบไม่ใช่ action หลักของหน้า จึงเป็น secondary ไม่ใช่ primary
              จอแคบเหลือแค่ไอคอน — aria-label คงความหมายไว้ให้ screen reader */}
          <button
            type="button"
            onClick={() => void onLogout()}
            className="btn-secondary btn-block app-sidebar__logout"
            aria-label="ອອກຈາກລະບົບ"
          >
            <LogOut size={16} />
            <span>ອອກຈາກລະບົບ</span>
          </button>
        </div>
      </aside>

      <main className="app-main">
        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
